import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Hammer, Phone, Mail, Clock, Shield, Award, ArrowLeft, CheckCircle2, Star, Users, Wrench, PaintBucket, Home, Droplets } from 'lucide-react';
import { toast } from 'sonner';

const HandwerkerDemo = () => {
  const handleCall = () => {
    toast.info('📞 Anruf', { description: 'Tel: 030 123 456' });
  };
  
  const handleQuote = () => {
    toast.success('✅ Angebotsanfrage gesendet!', { 
      description: 'Wir melden uns innerhalb von 24 Stunden.' 
    });
  };
  const services = [
    { icon: PaintBucket, title: 'Malerarbeiten', desc: 'Innen- und Außenanstriche, Tapezieren, Lackierarbeiten' },
    { icon: Home, title: 'Trockenbau', desc: 'Wände, Decken, Dämmung, Schallschutz' },
    { icon: Droplets, title: 'Badsanierung', desc: 'Komplettbäder, Fliesen, Sanitärinstallation' },
    { icon: Wrench, title: 'Renovierung', desc: 'Böden, Türen, Fenster, Komplettrenovierung' },
  ];

  const projects = [
    { title: 'Altbausanierung Prenzlauer Berg', type: 'Komplettsanierung', img: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&auto=format' },
    { title: 'Badezimmer Modernisierung', type: 'Badsanierung', img: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=600&auto=format' },
    { title: 'Büroräume Charlottenburg', type: 'Innenausbau', img: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=600&auto=format' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white shadow-md">
            <ArrowLeft size={16} className="mr-2" />
            Zurück zum Portfolio
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
              <Hammer className="text-white" size={20} />
            </div>
            <span className="text-slate-900 font-bold text-lg">Meister Bau Berlin</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <a href="#leistungen" className="hover:text-orange-600">Leistungen</a>
            <a href="#projekte" className="hover:text-orange-600">Projekte</a>
            <a href="#kontakt" className="hover:text-orange-600">Kontakt</a>
          </div>
          <Button onClick={handleCall} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Phone size={16} className="mr-2" />
            030 123 456
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center bg-gradient-to-br from-orange-600 to-orange-800 pt-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80')] bg-cover bg-center opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 mb-6">
              <Award className="text-white" size={16} />
              <span className="text-white text-sm font-medium">Meisterbetrieb seit 1985</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight">
              Qualität aus Meisterhand
            </h1>
            <p className="text-xl text-orange-100 mb-8 leading-relaxed">
              Ihr zuverlässiger Partner für Renovierung, Sanierung und Neubau in Berlin und Brandenburg. Über 35 Jahre Erfahrung.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" onClick={handleCall} className="bg-white text-orange-600 hover:bg-orange-50 px-8">
                <Phone size={18} className="mr-2" />
                Jetzt anrufen
              </Button>
              <Button size="lg" onClick={handleQuote} variant="outline" className="border-white text-white hover:bg-white/10">
                Kostenloses Angebot
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="mt-12 flex flex-wrap gap-6">
              {[
                { icon: Shield, text: '5 Jahre Garantie' },
                { icon: Users, text: '2.500+ Kunden' },
                { icon: Star, text: '4.9/5 Bewertung' },
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-white">
                  <badge.icon size={20} className="text-orange-200" />
                  <span className="text-sm font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="leistungen" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Unsere Leistungen</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Alles aus einer Hand</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Von kleinen Reparaturen bis zum kompletten Umbau – wir sind Ihr kompetenter Partner.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <Card key={i} className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center mb-4 group-hover:bg-orange-500 transition-colors">
                    <service.icon className="text-orange-600 group-hover:text-white transition-colors" size={28} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projekte" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Referenzen</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Aktuelle Projekte</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {projects.map((project, i) => (
              <Card key={i} className="border-0 shadow-lg overflow-hidden group">
                <div className="h-56 overflow-hidden">
                  <img src={project.img} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <CardContent className="p-6">
                  <span className="text-orange-600 text-sm font-medium">{project.type}</span>
                  <h3 className="font-bold text-slate-900 mt-1">{project.title}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 bg-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: '35+', label: 'Jahre Erfahrung' },
              { value: '2.500+', label: 'Zufriedene Kunden' },
              { value: '100%', label: 'Termintreue' },
              { value: '5 Jahre', label: 'Garantie' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-orange-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="kontakt" className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Kontakt</span>
            <h2 className="text-4xl font-bold text-slate-900 mt-3">Kostenloses Angebot anfordern</h2>
          </div>
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <form className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Input placeholder="Ihr Name" className="bg-white" />
                  <Input type="email" placeholder="Ihre E-Mail" className="bg-white" />
                </div>
                <Input type="tel" placeholder="Ihre Telefonnummer" className="bg-white" />
                <textarea className="w-full h-32 px-4 py-3 bg-white border border-slate-200 rounded-lg resize-none" placeholder="Beschreiben Sie Ihr Projekt..."></textarea>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6">
                  Anfrage senden
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-slate-400">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                  <Hammer className="text-white" size={20} />
                </div>
                <span className="text-white font-bold">Meister Bau Berlin</span>
              </div>
              <p className="text-sm">Ihr Meisterbetrieb für Renovierung und Sanierung seit 1985.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Kontakt</h4>
              <p className="text-sm">Handwerkerstraße 42<br />10115 Berlin<br />Tel: 030 123 456</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Öffnungszeiten</h4>
              <p className="text-sm">Mo-Fr: 07:00 - 18:00<br />Sa: 08:00 - 14:00</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © 2024 Meister Bau Berlin. Alle Rechte vorbehalten.
          </div>
        </div>
      </footer>

      {/* Demo Badge */}
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-50">
        Demo-Projekt von Beta-Webagentur
      </div>
    </div>
  );
};

export default HandwerkerDemo;
